import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import styles from './CategoriesAndWarehouses.module.css'; // Make sure this CSS file is in the same folder

// Import services (assuming they are correctly set up)
import {
  getCategories, createCategory, updateCategory, deleteCategory,
  addSubCategory, updateSubCategory, deleteSubCategory,
} from "../../services/categoryService";
import {
  getWarehouses, createWarehouse, updateWarehouse, deleteWarehouse,
} from "../../services/warehouseService";
import {
  getDepartments, createDepartment, updateDepartment, deleteDepartment,
} from "../../services/departmentService";
import { showConfirmDialog } from "../../utilis/CustomMessages";

// Import icons for a cleaner UI
import { RiAddLine, RiPencilLine, RiDeleteBin6Line, RiSearchLine } from "react-icons/ri";

// A reusable Modal Component defined within this file for simplicity
const Modal = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>{title}</h3>
        {children}
      </div>
    </div>
  );
};

const CategoriesAndWarehouses = () => {
  // --- STATE MANAGEMENT ---
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Search terms for each column
  const [categorySearch, setCategorySearch] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [departmentSearch, setDepartmentSearch] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({});
  const [inputValue, setInputValue] = useState('');

  // --- DATA FETCHING ---
  const fetchData = async () => {
    try {
      // Use Promise.all for concurrent fetching
      const [catData, whData, depData] = await Promise.all([
        getCategories(),
        getWarehouses(),
        getDepartments(),
      ]);
      setCategories(catData);
      setWarehouses(whData);
      setDepartments(depData);
    } catch (error) {
      toast.error("Failed to load data. Please refresh the page.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- FILTERING LOGIC ---
  const filteredCategories = useMemo(() =>
    categories.filter(c =>
      c.name.toLowerCase().includes(categorySearch.toLowerCase())
    ), [categories, categorySearch]
  );
  const filteredWarehouses = useMemo(() =>
    warehouses.filter(w =>
      w.name.toLowerCase().includes(warehouseSearch.toLowerCase())
    ), [warehouses, warehouseSearch]
  );
  const filteredDepartments = useMemo(() =>
    departments.filter(d =>
      d.name.toLowerCase().includes(departmentSearch.toLowerCase())
    ), [departments, departmentSearch]
  );

  // --- MODAL & ACTIONS ---
  const openModal = (config) => {
    setModalConfig(config);
    setInputValue(config.item ? config.item.name : '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalConfig({});
    setInputValue('');
  };

  const handleSave = async () => {
    if (!inputValue.trim()) return toast.warn("Name cannot be empty.");

    const { mode, item, parentItem } = modalConfig;
    let promise;

    switch (mode) {
      case 'addCategory': promise = createCategory({ name: inputValue }); break;
      case 'editCategory': promise = updateCategory(item._id, { name: inputValue }); break;
      case 'addSub': promise = addSubCategory(parentItem._id, { name: inputValue }); break;
      case 'editSub': promise = updateSubCategory(parentItem._id, item._id, { name: inputValue }); break;
      case 'addWarehouse': promise = createWarehouse({ name: inputValue }); break;
      case 'editWarehouse': promise = updateWarehouse(item._id, { name: inputValue }); break;
      case 'addDepartment': promise = createDepartment({ name: inputValue }); break;
      case 'editDepartment': promise = updateDepartment(item._id, { name: inputValue }); break;
      default: return;
    }

    try {
      await promise;
      toast.success("Operation successful!");
      fetchData(); // Refresh all data
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred.");
    }
  };

  const handleDelete = async (type, id, parentId = null) => {
    const confirmed = await showConfirmDialog("Confirm Deletion", "Are you sure? This cannot be undone.");
    if (!confirmed) return;

    let promise;
    switch (type) {
      case 'category': promise = deleteCategory(id); break;
      case 'subCategory': promise = deleteSubCategory(parentId, id); break;
      case 'warehouse': promise = deleteWarehouse(id); break;
      case 'department': promise = deleteDepartment(id); break;
      default: return;
    }
    try {
      await promise;
      toast.success("Item deleted.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete item.");
    }
  };


  if (isLoading) {
    return <div className={styles.loadingContainer}>Loading management console...</div>;
  }

  // --- RENDER ---
  return (
    <div className={styles.pageContainer}>
      <h2 className={styles.pageTitle}>Category, Warehouse, & Department Management</h2>

      <div className={styles.mainContentGrid}>
        {/* CATEGORIES COLUMN */}
        <div className={styles.column}>
          <div className={styles.columnHeader}>
            <h3>Categories</h3>
            <button onClick={() => openModal({ mode: 'addCategory', title: 'Add New Category' })} className={styles.addButton}><RiAddLine /></button>
          </div>
          <div className={styles.searchContainer}>
            <RiSearchLine className={styles.searchIcon} />
            <input type="text" placeholder="Search categories..." value={categorySearch} onChange={e => setCategorySearch(e.target.value)} />
          </div>
          <ul className={styles.itemList}>
            {filteredCategories.map(cat => (
              <li key={cat._id} className={styles.itemCard}>
                <div className={styles.itemHeader}>
                  <span className={styles.itemName}>{cat.name}</span>
                  <div className={styles.buttonGroup}>
                    <button onClick={() => openModal({ mode: 'editCategory', title: `Edit ${cat.name}`, item: cat })}><RiPencilLine /></button>
                    <button onClick={() => handleDelete('category', cat._id)} className={styles.deleteBtn}><RiDeleteBin6Line /></button>
                  </div>
                </div>
                <div className={styles.subCategorySection}>
                  {cat.subcategories.map(sub => (
                    <span key={sub._id} className={styles.subCategoryPill}>
                      {sub.name}
                      <button onClick={() => openModal({ mode: 'editSub', title: `Edit ${sub.name}`, item: sub, parentItem: cat })}><RiPencilLine /></button>
                      <button onClick={() => handleDelete('subCategory', sub._id, cat._id)}><RiDeleteBin6Line /></button>
                    </span>
                  ))}
                  <button onClick={() => openModal({ mode: 'addSub', title: `Add Sub to ${cat.name}`, parentItem: cat })} className={styles.addSubPill}><RiAddLine /> Add Sub</button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* WAREHOUSES COLUMN */}
        <div className={styles.column}>
          <div className={styles.columnHeader}>
            <h3>Warehouses</h3>
            <button onClick={() => openModal({ mode: 'addWarehouse', title: 'Add New Warehouse' })} className={styles.addButton}><RiAddLine /></button>
          </div>
          <div className={styles.searchContainer}>
            <RiSearchLine className={styles.searchIcon} />
            <input type="text" placeholder="Search warehouses..." value={warehouseSearch} onChange={e => setWarehouseSearch(e.target.value)} />
          </div>
          <ul className={styles.itemList}>
            {filteredWarehouses.map(wh => (
              <li key={wh._id} className={styles.itemCard}>
                <div className={styles.itemHeader}>
                  <span className={styles.itemName}>{wh.name}</span>
                  <div className={styles.buttonGroup}>
                    <button onClick={() => openModal({ mode: 'editWarehouse', title: `Edit ${wh.name}`, item: wh })}><RiPencilLine /></button>
                    <button onClick={() => handleDelete('warehouse', wh._id)} className={styles.deleteBtn}><RiDeleteBin6Line /></button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* DEPARTMENTS COLUMN */}
        <div className={styles.column}>
          <div className={styles.columnHeader}>
            <h3>Departments</h3>
            <button onClick={() => openModal({ mode: 'addDepartment', title: 'Add New Department' })} className={styles.addButton}><RiAddLine /></button>
          </div>
          <div className={styles.searchContainer}>
            <RiSearchLine className={styles.searchIcon} />
            <input type="text" placeholder="Search departments..." value={departmentSearch} onChange={e => setDepartmentSearch(e.target.value)} />
          </div>
          <ul className={styles.itemList}>
            {filteredDepartments.map(dep => (
              <li key={dep._id} className={styles.itemCard}>
                <div className={styles.itemHeader}>
                  <span className={styles.itemName}>{dep.name}</span>
                  <div className={styles.buttonGroup}>
                    <button onClick={() => openModal({ mode: 'editDepartment', title: `Edit ${dep.name}`, item: dep })}><RiPencilLine /></button>
                    <button onClick={() => handleDelete('department', dep._id)} className={styles.deleteBtn}><RiDeleteBin6Line /></button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={modalConfig.title}>
        <div className={styles.modalBody}>
          <input
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            className={styles.modalInput}
            placeholder="Enter name..."
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleSave()}
          />
        </div>
        <div className={styles.modalFooter}>
          <button onClick={closeModal} className={styles.secondaryButton}>Cancel</button>
          <button onClick={handleSave} className={styles.primaryButton}>Save Changes</button>
        </div>
      </Modal>
    </div>
  );
};

export default CategoriesAndWarehouses;